import * as Yup from "yup";

/* eslint-disable no-invalid-this */

const isAfter = (end, start) => {
  if (!end || !start) return true;
  return new Date(end).getTime() > new Date(start).getTime();
};

const hasMapCoords = (loc) => {
  if (!loc || typeof loc !== "object") return false;
  const lat = Number(loc.lat);
  const lng = Number(loc.lng);
  return Number.isFinite(lat) && Number.isFinite(lng);
};

/** Company portal Step 1: management lists via `/company_portal/v1/lookups` when available. */
const getPortalBasicInformationValidationSchema = (messages) =>
  Yup.object().shape({
    image_url: Yup.mixed().notRequired(),
    name: Yup.string().trim().required(messages.eventName),
    address: Yup.string()
      .trim()
      .required(messages.locationName)
      .test(
        "location-name-resolves-on-map",
        messages.locationInvalid,
        function validateLocationText(value) {
          if (!value) return true;
          return hasMapCoords(this.parent.location);
        },
      ),
    description: Yup.string().trim().required(messages.eventBrief),
    event_type_id: Yup.mixed()
      .required(messages.eventType)
      .test(
        "portal-event-type-id",
        messages.eventType,
        (v) => v != null && v !== "" && Number.isFinite(Number(v)),
      ),
    location: Yup.object()
      .shape({
        address: Yup.string().nullable(),
        lat: Yup.mixed().nullable(),
        lng: Yup.mixed().nullable(),
        radius: Yup.number()
          .typeError(messages.boundaries)
          .positive(messages.boundariesPositive)
          .required(messages.boundaries),
        city_id: Yup.mixed().nullable(),
      })
      .test("location-on-map", messages.locationInvalid, (loc) =>
        hasMapCoords(loc),
      ),
    /**
     * Validated independently from `location` (rather than nested at
     * `location.city_id`) so its error can't be shadowed by the sibling
     * `location-on-map` object-level test — Yup/Formik can only hold one
     * error value per path, and both would otherwise collide on "location".
     */
    location_city_id: Yup.mixed().test(
      "portal-city-id-required",
      messages.city,
      function validateCityId() {
        const v = this.parent.location?.city_id;
        return v != null && v !== "" && Number.isFinite(Number(v));
      },
    ),
    event_start_at: Yup.string().required(messages.actualStart),
    event_end_at: Yup.string()
      .required(messages.actualEnd)
      .test(
        "actual-end-after-start",
        messages.endAfterStart,
        function validateEnd(value) {
          return isAfter(value, this.parent.event_start_at);
        },
      ),
    building_start_at: Yup.string().notRequired(),
    building_end_at: Yup.string()
      .notRequired()
      .test(
        "build-up-end-after-start",
        messages.endAfterStart,
        function validateEnd(value) {
          return isAfter(value, this.parent.building_start_at);
        },
      ),
    dismantling_start_at: Yup.string().notRequired(),
    dismantling_end_at: Yup.string()
      .notRequired()
      .test(
        "dismantling-end-after-start",
        messages.endAfterStart,
        function validateEnd(value) {
          return isAfter(value, this.parent.dismantling_start_at);
        },
      ),
    payment_period: Yup.string().required(),
    number_of_days: Yup.number()
      .transform((value, originalValue) => (originalValue === "" ? undefined : value))
      .when("payment_period", {
        is: "monthly",
        then: (schema) =>
          schema
            .typeError(messages.numberOfDays)
            .min(1, messages.numberOfDaysMin)
            .required(messages.numberOfDays),
        otherwise: (schema) => schema.notRequired(),
      }),
    paid_days_off: Yup.number()
      .transform((value, originalValue) => (originalValue === "" ? undefined : value))
      .when("payment_period", {
        is: "monthly",
        then: (schema) =>
          schema
            .typeError(messages.paidDaysOff)
            .min(0, messages.paidDaysOffMin)
            .test(
              "paid-days-off-lte-number-of-days",
              messages.paidDaysOffMaxDays,
              function validatePaidDaysOff(value) {
                if (value == null) return true;
                const numberOfDays = Number(this.parent.number_of_days);
                if (!Number.isFinite(numberOfDays)) return true;
                return Number(value) <= numberOfDays;
              },
            )
            .required(messages.paidDaysOff),
        otherwise: (schema) => schema.notRequired(),
      }),
    attendance_verification_method: Yup.string().required(
      messages.checkInMethod,
    ),
    projectManagers: Yup.array().of(
      Yup.object().shape({
        company_user_id: Yup.mixed().required(messages.projectManagers),
        responsibility: Yup.string()
          .trim()
          .required(messages.responsibilityRequired),
      }),
    ),
    client: Yup.object().shape({
      admin_id: Yup.mixed().nullable().notRequired(),
    }),
  });

export default getPortalBasicInformationValidationSchema;
